import type { EligibilityResult } from '@astrally/shared';
import type { MatchableProfile } from './profile.js';

/**
 * Hard eligibility checks. These are EXCLUSION rules — they never become
 * weighted compatibility signals. If any fails, the pair is simply not
 * considered.
 */

export interface HardFilterContext {
  /** uids blocked by or blocking the seeker. */
  blockedUids: Set<string>;
  /** uids to skip for other reasons (existing connections, dismissed, etc.). */
  excludeUids?: Set<string>;
  /** Minimum trust level to be recommendable. */
  minTrustLevel?: number;
}

const DEFAULT_MIN_TRUST = 0.25;

export function checkEligibility(
  seeker: MatchableProfile,
  candidate: MatchableProfile,
  ctx: HardFilterContext,
): EligibilityResult {
  const reasons: string[] = [];

  if (seeker.uid === candidate.uid) reasons.push('self');
  if (!seeker.ageConfirmed) reasons.push('seeker-age-unconfirmed');
  if (!candidate.ageConfirmed) reasons.push('candidate-age-unconfirmed');
  if (!candidate.discoverable) reasons.push('candidate-not-discoverable');
  if (candidate.recommendationsPaused) reasons.push('candidate-paused');
  if (seeker.recommendationsPaused) reasons.push('seeker-paused');
  if (ctx.blockedUids.has(candidate.uid)) reasons.push('blocked');
  if (ctx.excludeUids?.has(candidate.uid)) reasons.push('excluded');

  const minTrust = ctx.minTrustLevel ?? DEFAULT_MIN_TRUST;
  if (candidate.trustLevel < minTrust) reasons.push('candidate-low-trust');

  const sharedIntents = seeker.intents.filter((i) => candidate.intents.includes(i));
  if (sharedIntents.length === 0) reasons.push('no-shared-intent');

  const sharedLanguage = seeker.languages.some((l) => candidate.languages.includes(l));
  if (!sharedLanguage) reasons.push('no-shared-language');

  return { eligible: reasons.length === 0, reasons };
}
